import { useState } from "react"
import { Checkbox, IconButton, Menu } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import {
  ALL_COLUMN_KEYS,
  DEFAULT_HIDDEN_COLUMN_KEYS,
  CLASS_SITUATION_COLUMN_TREE,
  LOCKED_COLUMN_KEYS,
  collectLeaves,
} from "./classSituationColumnTree"

function TreeNode({ node, depth, visible, onToggleKeys, expanded, onToggleExpand }) {
  const leaves = node.children ? collectLeaves(node.children) : [node]
  const togglable = leaves.filter((leaf) => !leaf.locked).map((leaf) => leaf.key)
  const visibleCount = leaves.filter((leaf) => visible.includes(leaf.key)).length
  const checked = visibleCount === leaves.length
  const indeterminate = visibleCount > 0 && !checked
  const isOpen = expanded.includes(node.key)

  return (
    <>
      <MDBox display="flex" alignItems="center" pl={depth * 2.5} pr={1} sx={{ minHeight: 32 }}>
        {node.children ? (
          <IconButton size="small" onClick={() => onToggleExpand(node.key)}>
            {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <MDBox width={30} />
        )}
        <Checkbox
          size="small"
          checked={checked}
          indeterminate={indeterminate}
          disabled={!togglable.length}
          onChange={() => onToggleKeys(togglable, !checked)}
        />
        <MDTypography variant="button" fontWeight={node.children ? "medium" : "regular"}>
          {node.label}
        </MDTypography>
      </MDBox>
      {node.children &&
        isOpen &&
        node.children.map((child) => (
          <TreeNode
            key={child.key}
            node={child}
            depth={depth + 1}
            visible={visible}
            onToggleKeys={onToggleKeys}
            expanded={expanded}
            onToggleExpand={onToggleExpand}
          />
        ))}
    </>
  )
}

// Opt-in columns are only ever ticked by hand, so Show all leaves them as they are.
const SHOW_ALL_KEYS = ALL_COLUMN_KEYS.filter((key) => !DEFAULT_HIDDEN_COLUMN_KEYS.includes(key))

export default function ColumnTreeMenu({ anchorEl, onClose, visible, onChange }) {
  const [expanded, setExpanded] = useState([])
  const allVisible = SHOW_ALL_KEYS.every((key) => visible.includes(key))

  const toggleKeys = (keys, show) => {
    const next = show
      ? [...new Set([...visible, ...keys])]
      : visible.filter((key) => !keys.includes(key))
    onChange(next)
  }

  const toggleExpand = (key) =>
    setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ style: { maxHeight: 480, width: 320 } }}
    >
      <MDBox display="flex" justifyContent="flex-end" px={1.5} pb={1}>
        {allVisible ? (
          <MDButton size="small" variant="text" color="info" onClick={() => onChange(LOCKED_COLUMN_KEYS)}>
            Hide all
          </MDButton>
        ) : (
          <MDButton size="small" variant="text" color="info" onClick={() => onChange([...new Set([...visible, ...SHOW_ALL_KEYS])])}>
            Show all
          </MDButton>
        )}
      </MDBox>
      {CLASS_SITUATION_COLUMN_TREE.map((node) => (
        <TreeNode
          key={node.key}
          node={node}
          depth={0}
          visible={visible}
          onToggleKeys={toggleKeys}
          expanded={expanded}
          onToggleExpand={toggleExpand}
        />
      ))}
    </Menu>
  )
}
